import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import Button from 'epn-ui/umd/dist/components/Button';
import Chips from 'epn-ui/umd/dist/components/Chips';
import Select from 'epn-ui/umd/dist/components/Select';
import Input from 'epn-ui/umd/dist/components/InputText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { faCheck } from '@fortawesome/pro-light-svg-icons';
import { faFileImport } from '@fortawesome/pro-regular-svg-icons/faFileImport';
import { faUserCheck } from '@fortawesome/pro-regular-svg-icons/faUserCheck';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { faHeart } from '@fortawesome/pro-regular-svg-icons/faHeart';
import CheckboxAnt from 'antd/lib/checkbox';
import useI18n from '@i18n';
import TrafficSourceFilter from '@pages/offers/Filters/TrafficSourceFilter/TrafficSourceFilter';

// Api
import {
    IGetCountriesDataFormatted,
    IGetOffersCompilationsDataFormatted,
    IGetTrafficSourcesCategoriesFormatted,
    IGetTrafficSourcesDataFormatted,
} from '@api/types';
import apiHooks from '@apiHooks';
import CategoryOption from './CategoryFilter/CategoryOption';

const { useTrafficSources, useCountries, useTrafficSourcesCategories } = apiHooks;

const MIN_SEARCH_LENGTH = 2;

interface IOffersFiltersProps {
    values: {
        search: string;
        trafficSources: string[];
        allowedCountries: string[];
        compilationIds: string[];
        categoriesIds: (number | string)[];
        hasBonusProgram: boolean;
        myPrograms: boolean;
        suitablePlacements: boolean;
        labelIds: boolean | number[];
        hasFeed: boolean;
        typeId: string | number;
        order: string;
    };
    onChange: (fieldName: string, value: boolean | string | (string | number)[]) => void;
    onSearch: (value: string) => void;
    offersCompilationsData: IGetOffersCompilationsDataFormatted[];
    clearAllFilters: () => void;
    isAuth: boolean;
}

const Filters: React.FC<IOffersFiltersProps> = ({
    onChange,
    onSearch,
    values,
    offersCompilationsData,
    clearAllFilters,
    isAuth,
}) => {
    const { t, locale } = useI18n('Offers');
    const { t: tCommon } = useI18n('Common');
    const [isCategoriesShow, setIsCategoriesShow] = useState<boolean>(true);
    const [isTrafficSourceShow, setIsTrafficSourceShow] = useState(true);
    const [checkedCategoriesIds, setCheckedCategoriesIds] = useState<number[]>([]);
    const [checkedTrafficSourceIds, setCheckedTrafficSourceIds] = useState<string[]>([]);
    const [isExpandAllCategories, setIsExpandAllCategories] = useState(false);

    const {
        search: searchValue,
        labelIds,
        hasFeed,
        suitablePlacements,
        compilationIds,
        categoriesIds,
        trafficSources,
    } = values;

    useEffect(() => {
        if (
            categoriesIds?.length !== 0 &&
            ((checkedCategoriesIds?.[0]?.toString() === '0' && checkedCategoriesIds.length === 1) ||
                checkedCategoriesIds.length === 0)
        ) {
            setCheckedCategoriesIds(categoriesIds?.map(id => +id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoriesIds]);

    useEffect(() => {
        if (
            trafficSources?.length !== 0 &&
            ((checkedTrafficSourceIds?.[0]?.toString() === '0' &&
                checkedCategoriesIds.length === 1) ||
                checkedTrafficSourceIds?.length === 0)
        ) {
            setCheckedTrafficSourceIds(trafficSources);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trafficSources]);

    const [search, setSearch] = useState(searchValue);

    const { data: trafficSourcesData } = useTrafficSources<IGetTrafficSourcesDataFormatted[]>({
        optionalAuth: true,
        dependencies: [locale],
    });

    const { data: countriesData, isLoading: isLoadingCountries } = useCountries<
        IGetCountriesDataFormatted[]
    >({
        optionalAuth: true,
        dependencies: [locale],
    });

    const { data: categoryTraffic } = useTrafficSourcesCategories<
        IGetTrafficSourcesCategoriesFormatted[]
    >({
        optionalAuth: true,
        dependencies: [locale],
    });

    const categoriesOptions = useMemo(
        () =>
            offersCompilationsData
                .map(({ offersIds, name, id }: IGetOffersCompilationsDataFormatted) => ({
                    value: id,
                    label: name,
                    items: offersIds,
                    isChecked: checkedCategoriesIds?.includes(id),
                }))
                .filter(compilation => compilation?.value?.toString() !== '134') || [],
        [offersCompilationsData, checkedCategoriesIds],
    );
    const countriesOptions = useMemo(
        () =>
            countriesData?.map(({ value, label }) => ({
                value,
                label,
            })) || [],
        [countriesData],
    );

    const hasTrackingPromoCompilation = useMemo(() => {
        return !!offersCompilationsData.find(
            (compilation: IGetOffersCompilationsDataFormatted) => compilation?.id === 134,
        );
    }, [offersCompilationsData]);

    const getValue = (options, selected, name) => {
        if (!options) {
            return [];
        }
        return selected?.reduce((acc, select) => {
            const found = options?.find(option => {
                if (name === 'compilationIds') {
                    return Number(select) === Number(option.value);
                }
                return select === option.value;
            });

            if (found) {
                acc.push(found);
            }

            return acc;
        }, []);
    };

    const setCategoryFilters = (categoryId: number) => {
        let updateCheckedCategories = [];

        if (checkedCategoriesIds?.includes(categoryId)) {
            updateCheckedCategories = checkedCategoriesIds?.filter(id => categoryId !== id);
        } else {
            updateCheckedCategories = [...checkedCategoriesIds, categoryId];
        }

        const checkedOptions = getValue(
            categoriesOptions,
            updateCheckedCategories,
            'compilationIds',
        );

        onChange('ids', checkedOptions?.map(option => option.items.join(',')).join(','));
        onChange('categoriesIds', updateCheckedCategories);
        setCheckedCategoriesIds(updateCheckedCategories);
    };

    const setTrafficSourceFilters = (trafficSourceId: string) => {
        let updatedCheckedTrafficSourceIds = [];

        if (checkedTrafficSourceIds.find(id => +id === +trafficSourceId)) {
            updatedCheckedTrafficSourceIds = checkedTrafficSourceIds.filter(
                id => +id !== +trafficSourceId,
            );
        } else {
            updatedCheckedTrafficSourceIds = [...checkedTrafficSourceIds, trafficSourceId];
        }

        onChange('trafficSources', updatedCheckedTrafficSourceIds);
        setCheckedTrafficSourceIds(updatedCheckedTrafficSourceIds);
    };

    const onSuitablePlacementsChange = (value: boolean): void => {
        if (value) {
            setCheckedTrafficSourceIds([]);
            onChange('trafficSources', []);
        }

        onChange('suitablePlacements', value);
    };

    const onSearchChange = value => {
        const newValLength = value.length;
        const oldValLength = search?.length;

        setSearch(value);

        if (newValLength >= MIN_SEARCH_LENGTH || newValLength < oldValLength)
            return onSearch(value);

        return value;
    };

    const selectCountryFilterOption = (inputValue, option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase());

    const toggleCategories = () => {
        setIsCategoriesShow(!isCategoriesShow);
    };

    const toggleTrafficSource = () => {
        setIsTrafficSourceShow(!isTrafficSourceShow);
    };

    const toggleAllCategories = () => {
        setIsExpandAllCategories(prev => !prev);
    };

    const onCheckboxHandle = compilationId => {
        if (!compilationIds.includes(compilationId.toString())) {
            onChange('compilationIds', [...compilationIds, compilationId]);
        } else {
            onChange(
                'compilationIds',
                compilationIds.filter(compilation => compilation !== compilationId.toString()),
            );
        }
    };

    const clearFilters = () => {
        setCheckedTrafficSourceIds([]);
        setCheckedCategoriesIds([]);
        setSearch('');
        clearAllFilters();
    };

    return (
        <aside className="flex flex-col gap-[24px] mb-[32px]">
            <div className="flex flex-col gap-[16px]">
                <Input
                    placeholder={t('Search')}
                    prefix={<FontAwesomeIcon className="text-blueGray-500" icon={faSearch} />}
                    className="hidden mr-2 md:flex"
                    onChange={e => onSearchChange(e.target.value)}
                />
                <Select
                    id="OffersCountriesSelect"
                    options={countriesOptions}
                    onChange={selected => onChange('allowedCountries', selected)}
                    value={getValue(countriesOptions, values.allowedCountries, 'allowedCountries')}
                    filterOption={selectCountryFilterOption}
                    loading={isLoadingCountries}
                    placeholder={t('Geo')}
                    mode="multiple"
                    maxTagCount={1}
                    className="w-full"
                    notFoundContent={tCommon('Nothing found')}
                />
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-[8px]">
                {isAuth && (
                    <Chips
                        id="OffersExclusiveCheckbox"
                        label={t('Favorites')}
                        checked={!!labelIds}
                        size="small"
                        onChange={selected => onChange('labelIds', !!selected)}
                        icon={
                            labelIds ? (
                                <FontAwesomeIcon icon={faCheck} className="ml-[4px] mr-[3px]" />
                            ) : (
                                <FontAwesomeIcon icon={faHeart} className="ml-[3px]" />
                            )
                        }
                        className="justify-start w-full !rounded-[6px]"
                    />
                )}
                {hasTrackingPromoCompilation && (
                    <Chips
                        id="IsTrackingUsingPromotionalCodes"
                        label={t('Tracking promo codes')}
                        checked={compilationIds.includes('134')}
                        size="small"
                        onChange={() => onCheckboxHandle('134')}
                        icon={
                            compilationIds.includes('134') ? (
                                <FontAwesomeIcon icon={faCheck} className="ml-[3px] mr-[3px]" />
                            ) : (
                                <FontAwesomeIcon icon={faUserCheck} />
                            )
                        }
                        className="justify-start w-full !rounded-[6px]"
                    />
                )}
                <Chips
                    id="OffersExportOfGoodsCheckbox"
                    label={t('Export of goods')}
                    checked={hasFeed}
                    size="small"
                    onChange={selected => onChange('hasFeed', selected)}
                    icon={
                        hasFeed ? (
                            <FontAwesomeIcon icon={faCheck} className="ml-[3px] mr-[3px]" />
                        ) : (
                            <FontAwesomeIcon icon={faFileImport} className="mr-[4px]" />
                        )
                    }
                    className="justify-start w-full !rounded-[6px]"
                />
            </div>
            {isAuth && (
                <div>
                    <CheckboxAnt
                        id="IsSuitableForThePlacement"
                        name="IsSuitableForThePlacement"
                        className="text-blueGray-500 active:text-blueGray-800"
                        checked={suitablePlacements}
                        onChange={value => onSuitablePlacementsChange(value.target.checked)}
                    >
                        <span
                            className={`${
                                suitablePlacements ? 'text-blueGray-800' : 'text-blueGray-500}'
                            }`}
                        >
                            {t('Suitable for the placement')}
                        </span>
                    </CheckboxAnt>
                </div>
            )}
            <div>
                <button
                    type="button"
                    className="flex items-center justify-between w-full font-medium text-blueGray-600 text-[16px] leading-[21px]"
                    onClick={toggleCategories}
                >
                    <span>{t('Categories')}</span>
                    <FontAwesomeIcon icon={isCategoriesShow ? faChevronUp : faChevronDown} />
                </button>

                {isCategoriesShow && (
                    <>
                        <div
                            className={clsx({
                                'md:flex-col mt-[-8px] pt-5': true,
                                'overflow-hidden max-h-[144px] md:max-h-[297px]': !isExpandAllCategories,
                            })}
                        >
                            <div className="flex flex-row flex-wrap md:flex-col gap-[8px]">
                                {categoriesOptions?.length &&
                                    categoriesOptions?.map(category => (
                                        <CategoryOption
                                            key={category.value}
                                            categoryOption={category}
                                            onChange={setCategoryFilters}
                                        />
                                    ))}
                            </div>
                        </div>
                        <Button
                            size="small"
                            className={clsx(
                                'w-full !text-blueGray-500 !bg-blueGray-100 my-[8px] rounded-[6px] !border-blueGray-100',
                                {
                                    'sm:hidden': categoriesOptions.length < 11,
                                },
                            )}
                            type="default"
                            onClick={toggleAllCategories}
                        >
                            <p>{isExpandAllCategories ? t('Roll up') : t('Expand all')}</p>
                            <FontAwesomeIcon
                                icon={isExpandAllCategories ? faChevronUp : faChevronDown}
                            />
                        </Button>
                    </>
                )}
            </div>
            <div>
                <button
                    type="button"
                    className="flex items-center justify-between w-full font-medium text-blueGray-600 text-[16px] leading-[21px] mb-[12px]"
                    onClick={toggleTrafficSource}
                >
                    <span>{t('Traffic type')}:</span>
                    <FontAwesomeIcon
                        icon={isTrafficSourceShow ? faChevronUp : faChevronDown}
                        className="text-blueGray-600"
                    />
                </button>

                {isTrafficSourceShow && (
                    <TrafficSourceFilter
                        categoryTraffic={categoryTraffic}
                        trafficSourcesData={trafficSourcesData}
                        checkedTrafficSourceIds={trafficSources}
                        onChange={setTrafficSourceFilters}
                    />
                )}
            </div>
            <Button type="default" className="w-full" onClick={clearFilters}>
                {t('Clear filters')}
            </Button>
        </aside>
    );
};

export default Filters;
